/**
 * VoiceRecommendationCard — luxury card shown inside Voice AURI when AURI
 * calls `altura_recommend`. Compact on mobile. Non-interruptive: the visitor
 * keeps talking to AURI while browsing the card.
 */
import { MapPin, ExternalLink, MessageSquare, PhoneCall } from "lucide-react";

const rangeLabel = (r) => (r?.price ? `${r.currency || "USD"} ${Number(r.price).toLocaleString()}` : "Price on request");
const humanScore = (s) => (s >= 80 ? "Strong match" : s >= 60 ? "Relevant match" : "Possible match");

export default function VoiceRecommendationCard({ item, onView, onAsk, onRequestInfo, onDismiss, onOutcome }) {
  if (!item) return null;
  const isOpp = item.type === "opportunity";
  const isArticle = item.type === "article";
  const isCountry = item.type === "country";

  const click = async (kind, cb) => {
    try { await onOutcome?.(kind, item); } catch (e) { void e; }
    cb?.();
  };

  return (
    <div className="voice-reco" data-testid={`voice-reco-card-${item.id || item.slug || item.title}`}>
      {item.image_url ? (
        <div className="voice-reco__image">
          <img src={item.image_url} alt="" loading="lazy" />
          <span className="voice-reco__type">{item.type}</span>
        </div>
      ) : (
        <div className="voice-reco__image voice-reco__image--placeholder"><span className="voice-reco__type">{item.type}</span></div>
      )}
      <div className="voice-reco__body">
        <div className="voice-reco__eyebrow">
          <span>{humanScore(item.score)}</span>
          {item.country ? <span className="voice-reco__loc"><MapPin className="h-3 w-3" /> {[item.city, item.country].filter(Boolean).join(", ")}</span> : null}
        </div>
        <div className="voice-reco__title">{item.title}</div>
        {isOpp && <div className="voice-reco__price">{rangeLabel(item)}</div>}
        {item.public_summary ? <div className="voice-reco__summary">{item.public_summary}</div> : null}
        {(item.match_reasons || []).length > 0 && (
          <ul className="voice-reco__reasons">
            {item.match_reasons.slice(0, 3).map((r, i) => <li key={i}>• {r}</li>)}
          </ul>
        )}
        {(item.considerations || []).length > 0 && (
          <div className="voice-reco__cons">
            <span className="voice-reco__cons-label">Considerations · </span>{item.considerations.join(" · ")}
          </div>
        )}
        <div className="voice-reco__actions">
          {(isOpp || isArticle || isCountry) && (
            <a
              href={item.url}
              target="_blank" rel="noopener noreferrer"
              onClick={() => click("clicked", () => onView?.(item))}
              className="voice-reco__btn voice-reco__btn--primary"
              data-testid={`voice-reco-view-${item.id}`}
            >
              {isOpp ? "View Opportunity" : isArticle ? "Read Article" : "Explore Market"} <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {isOpp && (
            <>
              <button
                type="button"
                onClick={() => click("more_info", () => onAsk?.(item))}
                className="voice-reco__btn voice-reco__btn--ghost"
                data-testid={`voice-reco-ask-${item.id}`}
              >
                <MessageSquare className="h-3 w-3" /> Ask AURI About This
              </button>
              <button
                type="button"
                onClick={() => click("consultation", () => onRequestInfo?.(item))}
                className="voice-reco__btn voice-reco__btn--ghost"
                data-testid={`voice-reco-request-${item.id}`}
              >
                <PhoneCall className="h-3 w-3" /> Request Information
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => click("declined", () => onDismiss?.(item))}
            className="voice-reco__close"
            aria-label="Dismiss"
            data-testid={`voice-reco-dismiss-${item.id}`}
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
